import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Segmented, Input, Select, Pagination, Button, Empty, Spin, Tag, Tooltip, message } from 'antd';
import { useSelector } from 'react-redux';
import axios from 'axios';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeImage, NdoeError,
} from '../../components/ndoe';
import './hauteurTalon.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

/**
 * Saisie rapide de la HAUTEUR DE TALON sur les modèles (demande proprio 20/07).
 *
 * Principe : une grille de vignettes ; sous chaque modèle, les 5 niveaux en
 * pastilles cliquables — UN clic qualifie le modèle (enregistré aussitôt).
 * Pour les catégories plates (ballerines, babouches…), le niveau « Plat » est
 * SUGGÉRÉ en pointillé : un clic le confirme, rien n'est écrit sans ce geste.
 * Mode LOT : cochez plusieurs modèles, choisissez un niveau, appliquez d'un coup.
 */
export default function HauteurTalon() {
  const user = useSelector((s) => s.user?.currentUser);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';

  const [niveaux, setNiveaux] = useState([]);
  const [q, setQ] = useState('');
  const [categorie, setCategorie] = useState(null);
  const [categories, setCategories] = useState([]);
  const [etat, setEtat] = useState('a_renseigner');   // tous | a_renseigner | renseignes
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 24, a_renseigner_global: 0 });
  const [loading, setLoading] = useState(true);
  const [writing, setWriting] = useState([]);          // ids en écriture (par carte)
  const [lotBusy, setLotBusy] = useState(false);       // écriture de lot en cours
  const [coches, setCoches] = useState([]);            // ids sélectionnés (mode lot)
  const seq = useRef(0);

  // Niveaux + catégories (une fois).
  useEffect(() => {
    axios.get(`${DOMAIN}/api/produit/hauteur-talon/niveaux`).then(({ data: d }) => setNiveaux(d.niveaux || [])).catch(() => {});
    axios.get(`${DOMAIN}/api/rapport/produits/evolution/catalogue`, { params: { type: 'produit', page: 1 } })
      .then(({ data: d }) => setCategories(d.categories || [])).catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [q, categorie, etat]);

  const charger = useCallback(async () => {
    const mien = ++seq.current;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${DOMAIN}/api/produit/hauteur-talon/produits`, {
        params: { q: q.trim() || undefined, categorie: categorie || undefined, etat, page, page_size: 24 },
      });
      if (mien !== seq.current) return;
      setData(d);
    } catch (e) {
      if (mien === seq.current) setData({ items: [], total: 0, page_size: 24, a_renseigner_global: 0 });
    } finally {
      if (mien === seq.current) setLoading(false);
    }
  }, [q, categorie, etat, page]);
  useEffect(() => { const t = setTimeout(charger, q ? 320 : 0); return () => clearTimeout(t); }, [charger, q]);

  const niveauParId = useMemo(() => new Map(niveaux.map((n) => [n.id, n])), [niveaux]);
  const niveauParNom = useMemo(() => new Map(niveaux.map((n) => [n.nom, n])), [niveaux]);

  // Écriture d'un modèle (ou d'un lot). onLocal met à jour l'affichage sans
  // recharger, pour que la qualification soit instantanée à l'œil.
  const affecter = useCallback(async (ids, idHauteur) => {
    const liste = [...new Set((Array.isArray(ids) ? ids : [ids]).map(Number))];
    const estLot = liste.length > 1;
    // Anti-double-envoi : un clic sur une carte déjà en cours d'écriture (ou
    // pendant un lot) est ignoré — l'ancien verrou scalaire réactivait la
    // première carte dès qu'une seconde partait.
    if (estLot ? lotBusy : liste.some((id) => writing.includes(id))) return;
    if (estLot) setLotBusy(true); else setWriting((w) => [...w, ...liste]);
    try {
      await axios.patch(`${DOMAIN}/api/produit/hauteur-talon`, { ids: liste, id_hauteur_talon: idHauteur });
      const niv = idHauteur ? niveauParId.get(idHauteur) : null;
      setData((prev) => {
        // Le compteur « à renseigner » suit RÉELLEMENT les écritures : chaque
        // modèle qui passe de non-renseigné → renseigné le fait baisser (et
        // l'inverse quand on efface). Sans ça, la pastille bougeait mais le
        // badge restait figé.
        let delta = 0;
        const items = prev.items.map((it) => {
          if (!liste.includes(it.id)) return it;
          const avant = !!it.id_hauteur_talon;
          const apres = !!idHauteur;
          if (!avant && apres) delta -= 1;
          if (avant && !apres) delta += 1;
          return { ...it, id_hauteur_talon: idHauteur, hauteur_nom: niv ? niv.nom : null, suggestion: idHauteur ? null : it.suggestion };
        });
        // Les cartes qualifiées RESTENT visibles (retour immédiat + possibilité
        // de corriger) même en vue « à renseigner » ; elles disparaîtront au
        // prochain filtrage/rechargement.
        return { ...prev, items, a_renseigner_global: Math.max(0, (prev.a_renseigner_global || 0) + delta) };
      });
      message.success(estLot ? `${liste.length} modèles qualifiés` : 'Hauteur enregistrée');
      if (estLot) setCoches([]);
    } catch (e) {
      message.error(e.response?.data?.error || 'Enregistrement impossible.');
    } finally {
      if (estLot) setLotBusy(false); else setWriting((w) => w.filter((id) => !liste.includes(id)));
    }
  }, [niveauParId, lotBusy, writing]);

  const basculerCoche = (id) => setCoches((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  if (!isAdmin) {
    return (
      <NdoePage>
        <NdoePageHeader eyebrow="Catalogue" title="Hauteur de talon" />
        <NdoeError title="Accès réservé" text="La qualification du catalogue est réservée à l'administration." />
      </NdoePage>
    );
  }

  const optCategories = categories.map((c) => ({ value: c.id, label: c.nom }));

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Catalogue et stock"
        title="Hauteur de talon"
        subtitle="Qualifiez vos modèles pour affiner le profil des chaussures que vos clientes achètent — un clic par modèle, ou par lot."
      />

      <div className="ht-barre">
        <Input.Search allowClear placeholder="Nom ou code…" value={q}
          onChange={(e) => setQ(e.target.value)} className="ht-recherche" />
        <Select allowClear placeholder="Catégorie" value={categorie} onChange={setCategorie}
          options={optCategories} className="ht-filtre" showSearch optionFilterProp="label" />
        <Segmented value={etat} onChange={setEtat} options={[
          { value: 'a_renseigner', label: `À renseigner${data.a_renseigner_global ? ` (${data.a_renseigner_global})` : ''}` },
          { value: 'renseignes', label: 'Déjà faits' },
          { value: 'tous', label: 'Tous' },
        ]} />
      </div>

      {/* Barre d'action de LOT — apparaît dès qu'un modèle est coché. */}
      {coches.length > 0 && (
        <div className="ht-lot">
          <b>{coches.length} modèle(s) sélectionné(s)</b>
          <span>→ appliquer :</span>
          {niveaux.map((n) => (
            <Button key={n.id} size="small" loading={lotBusy} onClick={() => affecter(coches, n.id)}>
              {n.nom}
            </Button>
          ))}
          <Button size="small" type="text" onClick={() => setCoches([])}>Annuler</Button>
        </div>
      )}

      {loading ? (
        <div className="ht-attente"><Spin /></div>
      ) : !data.items.length ? (
        <Empty description="Aucun modèle pour ces filtres" />
      ) : (
        <div className="ht-grille">
          {data.items.map((it) => {
            const coche = coches.includes(it.id);
            const suggId = it.suggestion ? (niveauParNom.get(it.suggestion) || {}).id : null;
            return (
              <div key={it.id} className={`ht-carte ${it.id_hauteur_talon ? 'is-fait' : ''} ${coche ? 'is-coche' : ''}`}>
                <button type="button" className="ht-carte__coche" title="Sélectionner (mode lot)"
                  onClick={() => basculerCoche(it.id)}>{coche ? '✓' : ''}</button>
                <NdoeImage src={it.img} alt={it.nom_produit} size="md" preview={false} />
                <div className="ht-carte__nom" title={it.nom_produit}>{it.nom_produit}</div>
                <div className="ht-carte__cat">{[it.categorie, it.marque].filter(Boolean).join(' · ')}</div>

                <div className="ht-niveaux">
                  {niveaux.map((n) => {
                    const actif = it.id_hauteur_talon === n.id;
                    const suggere = !it.id_hauteur_talon && suggId === n.id;
                    return (
                      <Tooltip key={n.id} title={n.fourchette}>
                        <button type="button"
                          className={`ht-pastille ${actif ? 'is-actif' : ''} ${suggere ? 'is-suggere' : ''}`}
                          disabled={lotBusy || writing.includes(it.id)}
                          onClick={() => affecter(it.id, actif ? null : n.id)}>
                          {n.nom}
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
                {it.id_hauteur_talon ? (
                  <div className="ht-actuel">✓ {it.hauteur_nom}</div>
                ) : it.suggestion ? (
                  <div className="ht-sugg">Suggéré : <b>{it.suggestion}</b> — cliquez pour confirmer</div>
                ) : <div className="ht-vide">À définir</div>}
              </div>
            );
          })}
        </div>
      )}

      {data.total > data.page_size && (
        <div className="ht-pages">
          <Pagination current={page} total={data.total} pageSize={data.page_size}
            onChange={setPage} showSizeChanger={false} showTotal={(t) => `${t} modèle(s)`} />
        </div>
      )}

      <p className="ht-note">
        Échelle standard : <Tag>Plat 0–2</Tag><Tag>Petit talon 2–5</Tag><Tag>Talon moyen 5–8</Tag>
        <Tag>Haut talon 8–10</Tag><Tag>Très haut 10+</Tag> (cm). Ce critère est facultatif et
        n'affecte ni le stock ni les ventes — il enrichit seulement l'analyse.
      </p>
    </NdoePage>
  );
}
